import { useState } from "react";
import { Link } from "react-router-dom";

import { AccountIcon, CloseIcon, InformationIcon } from "@/assets/icons";
import TabSelect from "@/components/tabSelect";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import Tooltip from "@/components/ui/tooltip";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import {
  DeleteGroupPayload,
  GetGroupByIdDetail,
  GroupPanelMode
} from "@/types/groups";

import {
  getViewGroupUserPermissionMapping,
  PermissionInfoTooltipContent,
  VIEW_GROUP_TAB_ASSIGNED_USERS_HEADER,
  VIEW_GROUP_TAB_OPTIONS
} from "../data";

type ViewGroupPanelProps = {
  groupDetailById: GetGroupByIdDetail;
  setGroupPanelMode: (mode: GroupPanelMode) => void; // Function to set the group panel mode
  onDelete: (data: DeleteGroupPayload) => Promise<void>;
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

type TabModuleProps = {
  groupDetailById: GetGroupByIdDetail;
};
const TabModule = ({ groupDetailById }: TabModuleProps) => (
  <ScrollArea className="rounded-lg border">
    <Table className="text-xs">
      <TableHeader className="flex flex-col flex-1">
        <TableRow className="flex items-center bg-white-smoke">
          <TableHead className="flex items-center flex-1 sticky top-0">
            <strong>Feature/ Module</strong>
          </TableHead>
          <TableHead className="flex gap-2 items-center basis-[189px] sticky top-0">
            <strong>Permission</strong>
            <Tooltip
              content={<PermissionInfoTooltipContent />}
              position="bottom-center"
            >
              <button
                type="button" // Default button type is submit. Thus, need to set type="button" to prevent form submission
                aria-label="Permission Info View Group Panel"
                className="bg-transparent border-0 p-0"
              >
                <InformationIcon />
              </button>
            </Tooltip>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="flex flex-col flex-1">
        {groupDetailById?.features?.map((row) => (
          <TableRow className="flex flex-1 last:border-b-0" key={row.code}>
            <TableCell className="flex-1 flex items-center">
              <strong>{row.name}</strong>
            </TableCell>
            <TableCell className="flex-0 basis-[189px] text-center">
              {getViewGroupUserPermissionMapping({
                name: row.name,
                permission: row.permission
              })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </ScrollArea>
);

const TabAssignedUsers = ({ groupDetailById }: TabModuleProps) => (
  <>
    {groupDetailById?.users?.length ? (
      <ScrollArea className="rounded-lg border">
        <Table className="text-xs">
          <TableHeader>
            <TableRow className="bg-white-smoke">
              {VIEW_GROUP_TAB_ASSIGNED_USERS_HEADER.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.key === "permission" ? "w-[113px]" : ""}
                >
                  <strong>{column.title}</strong>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupDetailById.users.map((user) => (
              <TableRow className="last:border-b-0" key={user.id}>
                <TableCell>{user.fullname}</TableCell>
                {/* TODO: need to implement Edit User first to be able to use this */}
                <TableCell className="text-center text-royal-blue">
                  <Link to="/user-management">Edit User</Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    ) : (
      <div className="flex flex-col items-center justify-center h-full p-4 text-xl text-txtBody gap-6">
        <AccountIcon className="fill-dim-gray w-14 h-14" />
        There are no users assigned to this group yet.
      </div>
    )}
  </>
);

export const ViewGroupPanel = ({
  groupDetailById,
  setGroupPanelMode,
  onDelete,
  onClose
}: ViewGroupPanelProps) => {
  const [viewType, setViewType] = useState("module");
  const userPermissions = useGetUserPermission();
  const isEditGroupGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.EDIT];
  const isDeleteGroupGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][
      EModulePermission.DELETE
    ];

  return (
    <>
      <div className="sticky top-0 z-10 p-6">
        <div className="flex flex-row justify-between items-center">
          <p className="text-txtHeading font-sans font-bold text-[20px]">
            View Group
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              aria-label="Close View Group Panel"
              onClick={onClose}
              className="bg-transparent border-0 p-0"
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
        </div>
      </div>
      <div className="sticky flex flex-col px-6 gap-3">
        <div className="flex items-center justify-between gap-4">
          <label htmlFor="groupName" className="text-sm text-txtBody flex-1">
            Group Name
          </label>
          <div
            id="groupName"
            className="text-txtHeading font-semibold text-sm text-right flex-1"
          >
            {groupDetailById.name}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <label
            htmlFor="groupDescription"
            className="text-sm text-txtBody flex-1"
          >
            Description
          </label>
          <div
            id="groupDescription"
            className="text-txtHeading font-semibold text-sm text-right flex-1"
          >
            {groupDetailById.description}
          </div>
        </div>
        <div className="flex justify-between gap-4 my-1">
          <TabSelect
            className="w-full bg-transparent h-auto gap-2 mt-1"
            buttonClassName="w-full p-0 text-base"
            buttonSelectedClassName="!border-none p-0"
            wrapperListClassName="w-full gap-1"
            tabList={VIEW_GROUP_TAB_OPTIONS}
            selected={viewType}
            onSelect={setViewType}
            variant="underline"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col py-3 px-6 gap-3 overflow-auto">
        <div className="flex flex-col flex-1 justify-between gap-4">
          {viewType === "module" && (
            <TabModule groupDetailById={groupDetailById} />
          )}
          {viewType === "assignedUsers" && (
            <TabAssignedUsers groupDetailById={groupDetailById} />
          )}
        </div>
      </div>
      {(isDeleteGroupGranted || isEditGroupGranted) && (
        <div
          className="flex px-6 pb-6 pt-4 gap-3 justify-between"
          style={{
            boxShadow: "0px -4px 4px 0px rgba(0, 0, 0, 0.05)"
          }}
        >
          {isDeleteGroupGranted && (
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl text-base font-semibold py-3"
              size="lg"
              onClick={() => onDelete(groupDetailById)}
            >
              Delete
            </Button>
          )}
          {isEditGroupGranted && (
            <Button
              className="w-full rounded-xl text-base font-semibold py-3"
              size="lg"
              onClick={() => setGroupPanelMode("edit")}
            >
              Edit
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default ViewGroupPanel;
